import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCargoTypes } from '../data-provider';
import type { CargoType, CargoTypeFilters, CargoTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal cargo type lookup cache used to resolve cargo type references
 * without repeatedly requesting the same cargo type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const cargoTypesQueryOptions = (
  filters: Partial<CargoTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const cargoTypes = await fetchCargoTypes(filters, { signal });
      put(cargoTypes.list);
      return cargoTypes;
    },
    queryKey: CACHE_KEYS.cargoTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CARGO_TYPES_CACHE_KEY = btoa('internal-cargo-types-cache-key');

const INTERNAL_QUERY_CARGO_TYPES_KEY = cargoTypesQueryOptions({ codeDesc: INTERNAL_CARGO_TYPES_CACHE_KEY }).queryKey;

const getCargoTypeFallback = (cargoTypeId: string): CargoType => {
  logger.warn(`Cargo type "${cargoTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: cargoTypeId,
    id: cargoTypeId,
    label: cargoTypeId,
    lookupCodeType: 'CARGO',
  };
};

export const put = async (cargoTypes: CargoType[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARGO_TYPES_KEY);

  const newCargoTypes = cargoTypes.reduce(
    (acc, cargoType) => {
      acc[String(cargoType.id)] = cargoType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CARGO_TYPES_KEY, {
    list: Object.values(newCargoTypes),
    map: newCargoTypes,
  });
};

export const getAll = (): CargoTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CARGO_TYPES_KEY);
};

export const find = async (cargoTypeId: string): Promise<CargoType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARGO_TYPES_KEY);

  const cargoType = data?.map[cargoTypeId];
  if (cargoType) {
    return cargoType;
  }

  try {
    const newData = await queryClient.query(cargoTypesQueryOptions({ search: { codeId: cargoTypeId } }));
    if (newData.map[cargoTypeId]) {
      void put(newData.list);
      return newData.map[cargoTypeId];
    }
  } catch {
    return getCargoTypeFallback(cargoTypeId);
  }

  return getCargoTypeFallback(cargoTypeId);
};

export const findAll = async (cargoTypeIds: string[]): Promise<CargoType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARGO_TYPES_KEY);

  const missingIds: string[] = [];
  const cargoTypes: CargoType[] = [];

  for (const cargoTypeId of cargoTypeIds) {
    const c = data?.map[cargoTypeId];
    if (c) {
      cargoTypes.push(c);
    } else {
      missingIds.push(cargoTypeId);
    }
  }

  if (!missingIds.length) {
    return cargoTypes;
  }

  try {
    const newData = await queryClient.query(cargoTypesQueryOptions({ search: { codeId: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const c = newData.map[missingId];
      if (c) {
        cargoTypes.push(c);
      } else {
        cargoTypes.push(getCargoTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      cargoTypes.push(getCargoTypeFallback(missingId));
    }
  }

  return cargoTypes;
};
